import SharedPreferences from "../../constants/SharedPreferences";
import {
  getErrorMessage,
  saveInStorage,
  getFromStorage,
  createLMSGroupKey,
  getEncryptionCurveFromStorage,
  logApiResponse,
} from "../../util/Utility";
import { getAuthRestclient } from "../RestUtility";
import { aeadDecryptWithAssociatedData } from "../../crypto/AuthenticatedEncryption";
import BayunError from "../../constants/BayunError";
import Constants from "../../constants/Constants";
import { decryptAsymmetric } from "../../util/AsymmetricEncryption";
import {
  getMessageFromSignatureString,
  retrieveAndverifyLastSignature,
  verifyMessageForPublicKeyTag,
  verifyMemberPublicKey,
} from "../../util/SignatureUtil";
import SigningPublicKeyTags from "../../constants/SigningPublicKeyTags";
import { getMemberLockBox } from "./memberApi";
import { encodeKey, decodeKey } from "../../crypto/PBKDF2";
import { asRawKey } from "../../crypto/KeyMaterial";

async function isOrgAdminApprovalPolicy(sessionId) {
  return (
    (await getFromStorage(sessionId, SharedPreferences.KEY_VALIDATION_POLICY)) ===
    Constants.KEY_VALIDATION_POLICY.ORG_ADMIN_APPROVAL
  );
}

/**
 * This method calls the API to create a new group.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} groupName the optional name of the group.
 * @param {String} memberPublicKeyEncryptedGroupKey the groupKey encrypted with memberPublicKey of the creator.
 * @param {String} memberPublicKeyEncryptedGroupKey_kek the kek of the encryption of groupKey.
 * @returns {Promise} the response of which contains an object with the groupId and groupkey.
 */
export const createGroup = async (
  sessionId,
  groupName,
  memberPublicKeyEncryptedGroupKey,
  memberPublicKeyEncryptedGroupKey_kek,
  groupSigningPublicKey,
  groupSigningPrivateKeyMemberEncrypted,
  groupSigningPrivateKeyMemberEncrypted_kek,
  groupSigningPrivateKeyOrgEncrypted,
  adminEncryptedGroupInfoKey,
  adminEncryptedGroupInfoKey_kek,
  lmsEncryptedGroupInfoKey,
  lmsGroupKey,
  signedGroupType,
  signedGroupKey,
  signedGroupPrivateKey,
  encryptedGroupType,
  orgKeyEncryptedGroupKey,
  backdoorEncryptedGroupPrivateKey,
  backdoorEncryptedGroupPrivateKey_kek,
  backdoorEncryptedGroupKey,
  backdoorEncryptedGroupKey_kek,
) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);

  // call the createGroup API
  const request = {
    adminEncryptedGroupInfoKey,
    adminEncryptedGroupInfoKey_kek,
    orgEncryptedGroupPrivateKey: groupSigningPrivateKeyOrgEncrypted,
    orgKeyEncryptedGroupKey: orgKeyEncryptedGroupKey,
    memberEncryptedGroupPrivateKey: groupSigningPrivateKeyMemberEncrypted,
    memberEncryptedGroupPrivateKey_kek:
      groupSigningPrivateKeyMemberEncrypted_kek,
    memberPublicKeyEncryptedGroupKey,
    memberPublicKeyEncryptedGroupKey_kek,
    groupName,
    groupPublicKey: groupSigningPublicKey,
    groupType: encryptedGroupType,
    lmsEncryptedGroupInfoKey,
    lmsGroupKey:
      typeof lmsGroupKey === "string" ? lmsGroupKey : encodeKey(lmsGroupKey),
    signedGroupType,
    groupKeyDetailRequest: {
      signedGroupKey,
      signedGroupPrivateKey,
    },
    backdoorEncryptedGroupPrivateKey: backdoorEncryptedGroupPrivateKey,
    backdoorEncryptedGroupPrivateKey_kek: backdoorEncryptedGroupPrivateKey_kek,
    backdoorEncryptedGroupKey: backdoorEncryptedGroupKey,
    backdoorEncryptedGroupKey_kek: backdoorEncryptedGroupKey_kek,
  };

  const createGroupResponse = await authRestClient.post(
    "/group/create",
    request,
  );

  // console.log(createGroupResponse);
  await logApiResponse("createGroupResponse", createGroupResponse);

  return createGroupResponse.data;
};

/**
 * This method calls the API to get a list of all the joined groups along with their keys.
 *
 * @param {String} sessionId id of the session in question.
 */
export const getMyGroups = async (sessionId) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);

  // call the getMyGroups API
  const getMyGroupsResponse = await authRestClient.get("/group/myGroups");

  await logApiResponse("getMyGroupsResponse", getMyGroupsResponse);

  const memberPrivateKey = await getFromStorage(
    sessionId,
    SharedPreferences.MEMBER_PRIVATE_KEY,
  );
  const orgMemberId = await getFromStorage(
    sessionId,
    SharedPreferences.ORG_MEMBER_ID,
  );
  const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);

  const receivedMyGroups = getMyGroupsResponse.data;
  const joinedGroupsCache = [];

  for (let index = 0; index < receivedMyGroups.length; index++) {
    const group = receivedMyGroups[index];

    // IMPORTANT : skipping these verifications to improve performance, adding these verify to getGroupById to only verify one particular group
    // verify addingMemberPublicKey
    // if (group.addingMemberPublicKey) {
    //   // verify addingMemberPublicKey with BayunServerPublicKey
    //   await verifyMemberPublicKey(
    //     sessionId,
    //     group.addingMemberPublicKey,
    //     Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.GROUP_OPERATION,
    //     group.trustedAddingMemberPublicKeySignature,
    //     group.addingMemberOrgName,
    //     group.addingMemberOrgMemberId,
    //     group.addingMemberStatus,
    //     "",
    //     "",
    //   );
    // } else {
    //   console.log("addingMemberPublicKey not found");
    // }

    // // verify groupKeyKEK
    // if (group.groupKey_kek) {
    //   if (
    //     !(await verifyMessageForPublicKeyTag(
    //       sessionId,
    //       group.groupKey_kek,
    //       getMessageFromSignatureString(group.addingMemberPublicKey),
    //       SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
    //     ))
    //   ) {
    //     console.log("groupKeyKEK verification failed.");
    //     throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    //   }
    // } else {
    //   console.log("groupKeyKek not found");
    // }

    // // verify groupPrivateKeyKEK
    // if (group.groupPrivateKey_kek) {
    //   if (
    //     !(await verifyMessageForPublicKeyTag(
    //       sessionId,
    //       group.groupPrivateKey_kek,
    //       getMessageFromSignatureString(group.addingMemberPublicKey),
    //       SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
    //     ))
    //   ) {
    //     console.log("groupPrivateKey_kek verification failed.");
    //     throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    //   }
    // } else {
    //   console.log("groupPrivateKey_kek not found");
    // }

    // verify groupPublicKey
    // if (group.groupPublicKey) {
    //   if (
    //     !(await retrieveAndverifyLastSignature(sessionId, group.groupPublicKey))
    //   ) {
    //     console.log("groupPublicKey verification failed.");
    //     throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    //   }
    // } else {
    //   console.log("groupPublicKey not found");
    // }

    // decrpyt the groupKey with memberPrivateKey
    const decryptionResult = asRawKey(
      await decryptAsymmetric(
        group.groupKey,
        memberPrivateKey,
        getMessageFromSignatureString(group.groupKey_kek),
        orgMemberId,
        encryptionCurve,
      ),
    );

    // decrypt the group private signing key with memberPrivateKey
    const groupPrivateKey = await decryptAsymmetric(
      getMessageFromSignatureString(group.groupPrivateKey),
      memberPrivateKey,
      getMessageFromSignatureString(group.groupPrivateKey_kek),
      orgMemberId,
      encryptionCurve,
    );

    let groupName = "";

    // create lmsGroupKey
    const lmsGroupKey = await createLMSGroupKey(
      decryptionResult,
      group.creatorOrgName,
      group.creatorOrgMemberId,
    );

    // decrypt groupInfoKey using lmsGroupKey
    const groupInfoKey = decodeKey(
      await aeadDecryptWithAssociatedData(
        group.lmsEncryptedGroupInfoKey,
        Constants.LMS_KEY_PREFIX +
          group.creatorOrgName +
          group.creatorOrgMemberId,
        lmsGroupKey,
      ),
    );

    // decrypt Group type and name using groupInfoKey
    const baseId = group.creatorOrgName + group.creatorOrgMemberId;

    // group name is optional, can be null
    if (group.name) {
      groupName = await aeadDecryptWithAssociatedData(
        group.name,
        baseId,
        groupInfoKey,
      );

      if (!groupName) {
        console.log("Group Name Decryption Failed");
        throw BayunError.ERROR_GROUP_DECRYPTION_FAILED;
      }
    }

    // if (group.creatorMemberPublicKey) {
    //   await verifyMemberPublicKey(
    //     sessionId,
    //     group.creatorMemberPublicKey,
    //     Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.GROUP_OPERATION,
    //     group.trustedCreatingMemberPublicKeySignature,
    //     group.creatorOrgName,
    //     group.creatorOrgMemberId,
    //     group.creatorMemberStatus,
    //     "",
    //     "",
    //   );
    //   const creatorMemberPublicKey = getMessageFromSignatureString(
    //     group.creatorMemberPublicKey,
    //   );
    //   if (
    //     !(await verifyMessageForPublicKeyTag(
    //       sessionId,
    //       group.type,
    //       creatorMemberPublicKey,
    //       SigningPublicKeyTags.GROUP_CREATOR_PUBLIC_KEY_TAG,
    //     ))
    //   ) {
    //     console.log("EncryptedGroupType verification failed.");
    //     throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    //   }
    // }

    const encryptedGroupType = getMessageFromSignatureString(group.type);

    const groupTypeString = await aeadDecryptWithAssociatedData(
      encryptedGroupType,
      baseId,
      groupInfoKey,
    );

    if (!groupTypeString) {
      console.log("Group Type Decryption Failed");
      throw BayunError.ERROR_GROUP_DECRYPTION_FAILED;
    }

    joinedGroupsCache.push({
      id: group.id,
      key: encodeKey(decryptionResult),
      signingPublicKey: getMessageFromSignatureString(group.groupPublicKey),
      signingPrivateKey: groupPrivateKey,
      signedSigningPublicKey: group.groupPublicKey,
      creatorOrgName: group.creatorOrgName,
      creatorOrgMemberId: group.creatorOrgMemberId,
      groupName,
      groupType: groupTypeString,
    });
  }

  // save the joined groupKeys in cache
  await saveInStorage(
    sessionId,
    SharedPreferences.JOINED_GROUP_KEYS,
    JSON.stringify(joinedGroupsCache),
  );
};

/**
 * This method calls the API to get
 * @param {String} sessionId id of the session in question.
 * @param {String} groupId id of the group whose encrypted groupKey we are seeking.
 */
export const getOrgEncryptedGroupKey = async (sessionId, groupId) => {
  // get rest client
  const authRestClient = await getAuthRestclient(sessionId);

  try {
    // call the getOrgEncryptedGroupKey API
    const orgEncryptedGroupKeyResponse = await authRestClient.get(
      "group/public/groupKey",
      { params: { groupId } },
    );

    // console.log(orgEncryptedGroupKeyResponse);
    await logApiResponse(
      "orgEncryptedGroupKeyResponse",
      orgEncryptedGroupKeyResponse,
    );
    const encryptedGroupKey =
      orgEncryptedGroupKeyResponse.data.orgKeyEncryptedGroupKey;
    const baseId =
      orgEncryptedGroupKeyResponse.data.creatorOrgName +
      orgEncryptedGroupKeyResponse.data.creatorOrgMemberId;

    // get the orgKey
    let orgKey = asRawKey(
      await getFromStorage(sessionId, SharedPreferences.ORG_KEY),
    );

    if (!orgKey) {
      await getMemberLockBox(
        sessionId,
        await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
      );

      orgKey = asRawKey(
        await getFromStorage(sessionId, SharedPreferences.ORG_KEY),
      );
    }

    // Group key is encrypted with member's public key
    const decryptedGroupKey = decodeKey(
      await aeadDecryptWithAssociatedData(
        encryptedGroupKey,
        baseId,
        orgKey,
      ),
    );

    // Decrypt the groupPrivateKey with memberPrivateKey
    const groupSigningPrivateKey = await aeadDecryptWithAssociatedData(
      orgEncryptedGroupKeyResponse.data.groupPrivateKey,
      baseId,
      orgKey,
    );

    // Verify signature on groupPublicKey
    if (
      !(await retrieveAndverifyLastSignature(
        sessionId,
        orgEncryptedGroupKeyResponse.data.groupPublicKey,
      ))
    ) {
      console.log("groupPublicKey verification failed.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    return {
      key: asRawKey(decryptedGroupKey) || decryptedGroupKey,
      //getMessageFromSignatureString(
      publicKey: orgEncryptedGroupKeyResponse.data.groupPublicKey,
      //),
      privateKey: groupSigningPrivateKey,
      creatorOrgMemberId: orgEncryptedGroupKeyResponse.data.creatorOrgMemberId,
      creatorOrgName: orgEncryptedGroupKeyResponse.data.creatorOrgName,
    };
  } catch (error) {
    console.log(error);
    if (error?.response?.data) {
      console.log(error?.response?.data ?? error);
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    }
    throw error;
  }
};

/**
 * This method calls the API to get
 * @param {String} sessionId id of the session in question.
 * @param {String} groupId id of the group whose encrypted groupKey we are seeking.
 */
export const getGroupPublicKey = async (sessionId, groupId) => {
  // get rest client
  const authRestClient = await getAuthRestclient(sessionId);

  try {
    // call the getGroupPublicKey API
    const getGroupPublicKey = await authRestClient.get(
      "group/getGroupPublicKey",
      { params: { groupId } },
    );

    // console.log(getGroupPublicKey);
    await logApiResponse("getGroupPublicKey", getGroupPublicKey);
    await logApiResponse(
      "GroupPublicKey: ",
      getGroupPublicKey.data.groupPublicKey,
    );

    await saveInStorage(
      sessionId,
      SharedPreferences.GROUP_PUBLIC_KEY + groupId,
      await getMessageFromSignatureString(
        getGroupPublicKey.data.groupPublicKey,
      ),
    );

    return getGroupPublicKey.data.groupPublicKey;
  } catch (error) {
    console.log(error);
    if (error?.response?.data) {
      console.log(error?.response?.data ?? error);
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    }
    throw error;
  }
};

/**
 * This method calls the API to get unjoined public groups for an member.
 *
 * @param {String} sessionId id of the session in question.
 */
export const getUnjoinedGroupsAPI = async (sessionId) => {
  // get rest client
  const authRestClient = await getAuthRestclient(sessionId);

  // call the getUnjoinedGroups API
  const getUnjoinedGroupsResponse = await authRestClient.get("/group/unjoined");

  // console.log(getUnjoinedGroupsResponse);
  await logApiResponse("getUnjoinedGroupsResponse", getUnjoinedGroupsResponse);
  return getUnjoinedGroupsResponse.data;
};

export const joinPublicGroupAPI = async (
  sessionId,
  groupId,
  memberPublicKeyEncryptedGroupKey,
  memberPublicKeyEncryptedGroupKey_kek,
  memberEncryptedGroupPrivateKey,
  memberEncryptedGroupPrivateKey_kek,
  groupPublicKey,
  plainGroupKey,
  creatorOrgMemberId,
  creatorOrgName,
) => {
  creatorOrgMemberId = creatorOrgMemberId.toLowerCase();
  creatorOrgName = creatorOrgName.toLowerCase();
  // create and save lmsGroupKey
  try {
    const lmsGroupKey = await createLMSGroupKey(
      asRawKey(plainGroupKey) || plainGroupKey,
      creatorOrgName,
      creatorOrgMemberId,
    );

    // create a rest client
    const restClient = await getAuthRestclient(sessionId);

    // call the joinPublicGroup API
    const joinPublicGroupResponse = await restClient.post(
      "/group/joinPublicGroup",
      {
        memberEncryptedGroupPrivateKey,
        memberEncryptedGroupPrivateKey_kek,
        memberPublicKeyEncryptedGroupKey,
        memberPublicKeyEncryptedGroupKey_kek,
        groupId,
        groupPublicKey,
        lmsGroupKey: encodeKey(lmsGroupKey),
      },
    );

    await logApiResponse("joinPublicGroupResponse", joinPublicGroupResponse);
  } catch (error) {
    console.log(error);
    if (error?.response?.data) {
      console.log(error?.response?.data ?? error);
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    }
    throw error;
  }
};

/**
 * This method calls the API to get the memberPublicKey of the member in question.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} orgMemberId credentials of the member whose memberPublicKey is to be fetched.
 * @param {String} orgName credentials of the member whose memberPublicKey is to be fetched.
 */
export const getMemberPublicKey = async (sessionId, orgMemberId, orgName, purpose) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);
  const memberAppId = await getFromStorage(
    sessionId,
    SharedPreferences.MEMBER_APP_ID,
  );
  try {
    // call the getMemberPublicKey API
    const getMemberPublicKeyResponse = await authRestClient.post(
      "/memberPublicKey",
      {
        orgMemberId,
        orgName,
        memberAppId,
      },
    );

    // console.log(getMemberPublicKeyResponse);
    await logApiResponse(
      "getMemberPublicKeyResponse",
      getMemberPublicKeyResponse,
    );

    if (
      purpose ===
      Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.GROUP_OPERATION &&
      (await isOrgAdminApprovalPolicy(sessionId)) &&
      getMemberPublicKeyResponse.data.memberStatus ===
      Constants.MEMBER_STATUS.AUTO_APPROVED
    ) {
      throw BayunError.CANNOT_ADD_AUTO_APPROVED_MEMBER_TO_GROUP;
    }

    await verifyMemberPublicKey(
      sessionId,
      getMemberPublicKeyResponse.data.publicKey,
      purpose,
      getMemberPublicKeyResponse.data.trustedMemberPublicKeySignature,
      getMemberPublicKeyResponse.data.orgName,
      getMemberPublicKeyResponse.data.orgMemberId,
      getMemberPublicKeyResponse.data.memberStatus,
      getMemberPublicKeyResponse.data.appPublicKey,
      null,
      getMemberPublicKeyResponse.data.adminSigningPublicKey,
    );

    if (!getMemberPublicKeyResponse.data.trustedMemberPublicKeySignature) {
      await saveInStorage(
        sessionId,
        getMemberPublicKeyResponse.data.orgMemberId +
        "kIsTrustedSignatureExists",
        true,
      );
    }

    return getMemberPublicKeyResponse.data.publicKey;
  } catch (error) {
    console.log(error);
    if (error?.response?.data) {
      console.log(error?.response?.data ?? error);
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    }
    throw error;
  }
};

export const getMemberPublicKeys = async (sessionId, membersList, purpose) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);

  // call the getMemberPublicKey API
  const getMemberPublicKeysResponse = await authRestClient.post(
    "/memberPublicKeys",
    { memberPublicKeysRequest: membersList },
  );

  // console.log(getMemberPublicKeysResponse);
  await logApiResponse(
    "getMemberPublicKeysResponse",
    getMemberPublicKeysResponse,
  );

  //collecting members data whose verification failed
  let memberData = {};
  let trustedSignatureFailedForMembers = new Array();
  let memberApprovalVerificationKeySignatureFailedForMembers = new Array();
  let authorizationAppPublicKeyVerificationFailedForMembers = new Array();
  let bayunServerPublicKeyVerificationFailedForMembers = new Array();
  let adminPublicKeyVerificationFailedForMembers = new Array();
  let ghostMemberPublicKeyVerificationFailedForMembers = new Array();
  let memberPublicKeyCouldNotBeVerifiedForMembers = new Array();
  let autoApprovedMemberCannotBeAdded = new Array();
  const verifiedMemberPublicKeyResponses = [];

  for (
    let i = 0;
    i < getMemberPublicKeysResponse.data.memberPublicKeyResponse.length;
    i++
  ) {
    const memberPublicKeyResponse =
      getMemberPublicKeysResponse.data.memberPublicKeyResponse[i];

    memberData["orgMemberId"] = memberPublicKeyResponse.orgMemberId;
    memberData["orgName"] = memberPublicKeyResponse.orgName;

    if (
      purpose ===
      Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.GROUP_OPERATION &&
      (await isOrgAdminApprovalPolicy(sessionId)) &&
      memberPublicKeyResponse.memberStatus ===
      Constants.MEMBER_STATUS.AUTO_APPROVED
    ) {
      autoApprovedMemberCannotBeAdded.push({
        orgMemberId: memberPublicKeyResponse.orgMemberId.toLowerCase(),
        orgName: memberPublicKeyResponse.orgName.toLowerCase(),
      });
      continue;
    }

    await verifyMemberPublicKey(
      sessionId,
      memberPublicKeyResponse.publicKey,
      purpose,
      memberPublicKeyResponse.trustedMemberPublicKeySignature,
      memberPublicKeyResponse.orgName,
      memberPublicKeyResponse.orgMemberId,
      memberPublicKeyResponse.memberStatus,
      memberPublicKeyResponse.appPublicKey,
      null,
      memberPublicKeyResponse.adminSigningPublicKey,
    );
    if (!memberPublicKeyResponse.trustedMemberPublicKeySignature) {
      await saveInStorage(
        sessionId,
        memberPublicKeyResponse.orgMemberId + "kIsTrustedSignatureExists",
        true,
      );
    }
    verifiedMemberPublicKeyResponses.push(memberPublicKeyResponse);
  }

  //declearing the possible errors to log for members
  let errorMessage = [
    "TrustedMemberPublicKeySignature could not be verified by MemberPublicKey",
    "Member Approval Verification Key is Null",
    "Member Public Key could not be verified by AuthorizationAppPublicKey",
    "MemberPublicKey could not be verified by BayunServerPublicKey",
    "Admin Public Key could not be verified",
    "Ghost Member Public Key could not be verified",
    "Member public key could not be verified",
  ];

  let participantsList = [
    trustedSignatureFailedForMembers,
    memberApprovalVerificationKeySignatureFailedForMembers,
    authorizationAppPublicKeyVerificationFailedForMembers,
    bayunServerPublicKeyVerificationFailedForMembers,
    adminPublicKeyVerificationFailedForMembers,
    ghostMemberPublicKeyVerificationFailedForMembers,
    memberPublicKeyCouldNotBeVerifiedForMembers,
  ];

  // let AddParticipantErrObject = new Array();
  var AddParticipantErrObject = getMemberPublicKeysResponse.data.errorList;
  //creating a object of errors message with the respected error lists
  for (let i = 0; i < errorMessage.length; i++) {
    if (participantsList[i].length != 0) {
      const errorObject = {};
      errorObject["errorMessage"] = errorMessage[i];
      errorObject["participantsList"] = participantsList[i];
      AddParticipantErrObject.push(errorObject);
    }
  }

  if (autoApprovedMemberCannotBeAdded.length > 0) {
    AddParticipantErrObject.push({
      errorMessage: BayunError.CANNOT_ADD_AUTO_APPROVED_MEMBER_TO_GROUP,
      participantsList: autoApprovedMemberCannotBeAdded,
    });
  }

  const result = {};
  result["memberPublicKeyResponse"] = verifiedMemberPublicKeyResponses;
  result["AddParticipantErrObject"] = AddParticipantErrObject;
  return result;
};


/**
 * This function calls the API to add a new participant to the given group.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} orgMemberId credentials of the member to be added.
 * @param {String} orgName credentials of the member to be removed.
 * @param {String} memberKeyEncryptedGroupKey the groupKey encrypted with the
 *                                              memberPublicKey of the member to be added.
 * @param {String} memberKeyEncryptedGroupKey_kek the kek of the above groupKey encryption
 * @param {String} groupId id of the group to which the member is to be added.
 * @param {String} groupSigningPublicKey signing public key of the group.
 * @param {String} signedAdminPublicKey admin public key that is signed.
 * @param {String} signedMemberPublicKey signed memberPublicKey of participant being added.
 * @param {String} signedGroupKey signed groupKey.
 * @param {String} signedGroupPrivateKey signingPrivateKey of the group, encrypted and signed.
 */
export const addNewParticipantToGroupApiCall = async (
  sessionId,
  orgMemberId,
  orgName,
  groupSigningPrivateKeyMemberEncrypted,
  groupSigningPrivateKeyMemberEncrypted_kek,
  memberKeyEncryptedGroupKey,
  memberKeyEncryptedGroupKey_kek,
  groupId,
  groupSigningPublicKey,
  signedMemberPublicKey,
  signedGroupKey,
  signedGroupPrivateKey,
) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);

  // call the addParticipant API
  const addGroupParticipantResponse = await authRestClient.post(
    "/group/addParticipant",
    {
      orgMemberId,
      orgName,
      memberEncryptedGroupPrivateKey: groupSigningPrivateKeyMemberEncrypted,
      memberEncryptedGroupPrivateKey_kek:
        groupSigningPrivateKeyMemberEncrypted_kek,
      memberKeyEncryptedGroupKey,
      memberKeyEncryptedGroupKey_kek,
      groupId,
      groupPublicKey: groupSigningPublicKey,
      signedMemberPublicKey,
      signedGroupKey,
      signedGroupPrivateKey,
    },
  );

  // console.log(addGroupParticipantResponse);
  await logApiResponse(
    "addGroupParticipantResponse",
    addGroupParticipantResponse,
  );
};

export const addNewParticipantsToGroupApiCall = async (
  sessionId,
  requestObject,
) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);

  // call the addParticipant API
  const addGroupParticipantsResponse = await authRestClient.post(
    "/group/addParticipants",
    requestObject,
  );

  await logApiResponse(
    "addGroupParticipantsResponse",
    addGroupParticipantsResponse,
  );
  return addGroupParticipantsResponse;
};

/**
 * This function calls the API to remove a participant from the given group.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} orgMemberId credentials of the member to be removed.
 * @param {String} orgName credentials of the member to be removed.
 * @param {String} groupId id of the group from which the member is to be removed.
 */
export const removeParticipantOfGroup = async (
  sessionId,
  orgMemberId,
  orgName,
  groupId,
) => {
  orgMemberId = orgMemberId.toLowerCase();
  orgName = orgName.toLowerCase();
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);

  try {
    // call the removeParticipant API
    const removeParticipantFromGroupResponse = await authRestClient.post(
      "/group/removeParticipant",
      {
        orgMemberId,
        orgName,
        groupId,
      },
    );

    // console.log(removeParticipantFromGroupResponse);
    await logApiResponse(
      "removeParticipantFromGroupResponse",
      removeParticipantFromGroupResponse,
    );
  } catch (error) {
    console.log(error);
    if (error?.response?.data) {
      console.log(error?.response?.data ?? error);
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    }
    throw error;
  }
};

/**
 * This function calls the API to remove multiple participants from the given group.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} groupId id of the group from which the member is to be removed.
 * @param {String} participantsInfo credentials of the members to be removed.
 */
export const removeParticipantsOfGroup = async (
  sessionId,
  groupId,
  participantsInfo,
) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);

  for (var i = 0; i < participantsInfo.length; i++) {
    participantsInfo[i].orgMemberId =
      participantsInfo[i].orgMemberId.toLowerCase();
    participantsInfo[i].orgName = participantsInfo[i].orgName.toLowerCase();
  }

  if (participantsInfo.length != 0) {
    try {
      // call the removeParticipant API
      const removeParticipantsFromGroupResponse = await authRestClient.post(
        "/group/removeParticipants",
        {
          groupId,
          participantsInfo,
        },
      );

      // console.log(removeParticipantsFromGroupResponse);
      await logApiResponse(
        "removeParticipantsFromGroupResponse",
        removeParticipantsFromGroupResponse,
      );
    } catch (error) {
      console.log(error);
      if (error?.response?.data) {
        console.log(error?.response?.data ?? error);
        throw getErrorMessage(sessionId, error?.response?.data ?? error);
      }
      throw error;
    }
  } else {
    console.log("No group participants in the parameters.");
    throw BayunError.ERROR_NO_GROUP_PARTICIPANTS_IN_THE_PARAMETERS;
  }
};

/**
 * This function calls the API to remove all participants except from the given list.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} groupId id of the group from which the member is to be removed.
 * @param {String} participantsInfo credentials of the members to not be removed except others.
 * @param {Boolean} removeCallingParticipant boolean variale, has to be set true if we want to remove calling Participant itself, by default it is false.
 */
export const removeParticipantsFromGroupExceptList = async (
  sessionId,
  groupId,
  participantsInfo,
  removeCallingParticipant,
) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);
  //if(participantsInfo!=null){
  if (participantsInfo != null && participantsInfo.length != 0) {
    for (var i = 0; i < participantsInfo.length; i++) {
      participantsInfo[i].orgMemberId =
        participantsInfo[i].orgMemberId.toLowerCase();
      participantsInfo[i].orgName = participantsInfo[i].orgName.toLowerCase();
    }
  } else {
    participantsInfo = [];
  }
  try {
    // call the removeParticipant API
    const removeParticipantsFromGroupExceptListResponse =
      await authRestClient.post("/group/removeParticipantsExceptList", {
        groupId,
        participantsExceptionList: participantsInfo,
        removeCallingParticipant,
      });

    // console.log(removeParticipantsFromGroupExceptListResponse);
    await logApiResponse(
      "removeParticipantsFromGroupExceptListResponse",
      removeParticipantsFromGroupExceptListResponse,
    );
  } catch (error) {
    console.log(error);
    if (error?.response?.data) {
      console.log(error?.response?.data ?? error);
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    }
    throw error;
  }
};

/**
 * This function calls the API to get the current time form server in miliseconds.
 */
export const getCurrentTimeFromServer = async (sessionId) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);

  try {
    const getCurrentTime = await authRestClient.get("/getCurrentTime");

    await logApiResponse("Current Time From Server", getCurrentTime);
    return getCurrentTime;
  } catch (error) {
    console.log(error);
    if (error?.response?.data) {
      console.log(error?.response?.data ?? error);
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    }
    throw error;
  }
};

/**
 * This function deletes a group associated with the given groupId.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} groupId The id of the group to be deleted.
 */
export const deleteGroupWithId = async (sessionId, groupId) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);

  try {
    // call the deleteGroup API
    const deleteGroupResponse = await authRestClient.post(
      "/group/delete",
      null,
      {
        params: {
          groupId,
        },
      },
    );

    // console.log(deleteGroupResponse);
    await logApiResponse("deleteGroupResponse", deleteGroupResponse);
  } catch (error) {
    console.log(error);
    if (error?.response?.data) {
      console.log(error?.response?.data ?? error);
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    }
    throw error;
  }
};

export const getGroupById = async (sessionId, groupId) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);

  try {
    const getGroupByIdResponse = await authRestClient.get("group/groupById", {
      params: { groupId },
    });

    // console.log(getGroupByIdResponse);
    await logApiResponse("getGroupByIdResponse", getGroupByIdResponse);

    const joinedGroupsCache = [];

    var group = getGroupByIdResponse.data;
    const memberPrivateKey = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_PRIVATE_KEY,
    );
    const orgMemberId = await getFromStorage(
      sessionId,
      SharedPreferences.ORG_MEMBER_ID,
    );

    const orgName = await getFromStorage(sessionId, SharedPreferences.ORG_NAME);

    const creatorMemberPublicKey = getMessageFromSignatureString(
      group.creatorMemberPublicKey,
    );

    const keyValidationPolicy = await getFromStorage(sessionId, SharedPreferences.KEY_VALIDATION_POLICY);

    if (group.addingMemberPublicKey) {
      if (keyValidationPolicy === Constants.KEY_VALIDATION_POLICY.ORG_ADMIN_APPROVAL &&
        (group.addingMemberStatus === Constants.MEMBER_STATUS.AUTO_APPROVED ||
          group.addingMemberOrgName !== orgName)) {
        throw BayunError.ERROR_ORG_VALIDATION_POLICY_DOES_NOT_ALLOW_THIS_OPERATION;
      }
      await verifyMemberPublicKey(
        sessionId,
        group.addingMemberPublicKey,
        Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.GROUP_OPERATION,
        group.trustedAddingMemberPublicKeySignature,
        group.addingMemberOrgName,
        group.addingMemberOrgMemberId,
        group.addingMemberStatus,
        "",
        "",
        group.addingMemberOrgAdminPublicKey,
      );
    } else {
      console.log("addingMemberPublicKey not found");
    }

    if (!group.addingMemberSigningPublicKey) {
      console.log("addingMemberSigningPublicKey not found");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }
    const addingMemberSigningPublicKey = getMessageFromSignatureString(
      group.addingMemberSigningPublicKey,
    );

    // verify groupKeyKEK
    if (group.groupKey_kek) {
      if (
        !(await verifyMessageForPublicKeyTag(
          sessionId,
          group.groupKey_kek,
          addingMemberSigningPublicKey,
          SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
        ))
      ) {
        console.log("groupKeyKEK verification failed.");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    } else {
      console.log("groupKeyKek not found");
    }

    // verify groupPrivateKeyKEK
    if (group.groupPrivateKeyKek) {
      if (
        !(await verifyMessageForPublicKeyTag(
          sessionId,
          group.groupPrivateKeyKek,
          addingMemberSigningPublicKey,
          SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
        ))
      ) {
        console.log("groupPrivateKey_kek verification failed.");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    } else {
      console.log("groupPrivateKey_kek not found");
    }

    // verify groupPublicKey
    if (group.groupPublicKey) {
      if (
        !(await retrieveAndverifyLastSignature(sessionId, group.groupPublicKey))
      ) {
        console.log("groupPublicKey verification failed.");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    } else {
      console.log("groupPublicKey not found");
    }

    const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);

    const decryptionResult = asRawKey(
      await decryptAsymmetric(
        group.groupKey,
        memberPrivateKey,
        getMessageFromSignatureString(group.groupKey_kek),
        orgMemberId,
        encryptionCurve,
      ),
    );

    const groupPrivateKey = await decryptAsymmetric(
      getMessageFromSignatureString(group.groupPrivateKey),
      memberPrivateKey,
      getMessageFromSignatureString(group.groupPrivateKeyKek),
      orgMemberId,
      encryptionCurve,
    );

    let groupName = "";

    const lmsGroupKey = await createLMSGroupKey(
      decryptionResult,
      group.creatorOrgName,
      group.creatorOrgMemberId,
    );

    const groupInfoKey = decodeKey(
      await aeadDecryptWithAssociatedData(
        group.lmsEncryptedGroupInfoKey,
        Constants.LMS_KEY_PREFIX +
          group.creatorOrgName +
          group.creatorOrgMemberId,
        lmsGroupKey,
      ),
    );

    // decrypt Group type and name using groupInfoKey
    const baseId = group.creatorOrgName + group.creatorOrgMemberId;

    // group name is optional, can be null
    if (group.name) {
      groupName = await aeadDecryptWithAssociatedData(
        group.name,
        baseId,
        groupInfoKey,
      );

      if (!groupName) {
        console.log("Group Name Decryption Failed");
        throw BayunError.ERROR_GROUP_DECRYPTION_FAILED;
      }
    }

    if (group.creatorMemberPublicKey) {
      if (keyValidationPolicy === Constants.KEY_VALIDATION_POLICY.ORG_ADMIN_APPROVAL &&
        (group.creatorMemberStatus === Constants.MEMBER_STATUS.AUTO_APPROVED ||
          group.creatorOrgName !== orgName)) {
        throw BayunError.ERROR_ORG_VALIDATION_POLICY_DOES_NOT_ALLOW_THIS_OPERATION;
      }
      await verifyMemberPublicKey(
        sessionId,
        group.creatorMemberPublicKey,
        Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.GROUP_OPERATION,
        group.trustedCreatingMemberPublicKeySignature,
        group.creatorOrgName,
        group.creatorOrgMemberId,
        group.creatorMemberStatus,
        "",
        "",
        group.creatorMemberOrgAdminPublicKey,
      );
      if (!group.creatorMemberSigningPublicKey) {
        console.log("creatorMemberSigningPublicKey not found");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
      const creatorMemberSigningPublicKey = getMessageFromSignatureString(
        group.creatorMemberSigningPublicKey,
      );
      if (
        !(await verifyMessageForPublicKeyTag(
          sessionId,
          group.type,
          creatorMemberSigningPublicKey,
          SigningPublicKeyTags.GROUP_CREATOR_PUBLIC_KEY_TAG,
        ))
      ) {
        console.log("EncryptedGroupType verification failed.");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    }

    const encryptedGroupType = getMessageFromSignatureString(group.type);

    const groupTypeString = await aeadDecryptWithAssociatedData(
      encryptedGroupType,
      baseId,
      groupInfoKey,
    );

    if (!groupTypeString) {
      console.log("Group Type Decryption Failed");
      throw BayunError.ERROR_GROUP_DECRYPTION_FAILED;
    }

    var groupInfoByID = {
      groupId: group.id,
      groupName: groupName,
      groupType: groupTypeString,
      groupParticipants: group.groupParticipants,
    };

    joinedGroupsCache.push({
      id: group.id,
      key: encodeKey(decryptionResult),
      signingPublicKey: getMessageFromSignatureString(group.groupPublicKey),
      signingPrivateKey: groupPrivateKey,
      signedSigningPublicKey: group.groupPublicKey,
      creatorOrgName: group.creatorOrgName,
      creatorOrgMemberId: group.creatorOrgMemberId,
      groupName,
      groupType: groupTypeString,
    });

    await saveInStorage(
      sessionId,
      SharedPreferences.JOINED_GROUP_KEYS,
      JSON.stringify(joinedGroupsCache),
    );

    return groupInfoByID;
  } catch (error) {
    console.log(error);
    if (error?.response?.data) {
      console.log(error?.response?.data ?? error);
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    }
    throw error;
  }
};

/**
 * This function lets the logged in user leave the group associated with the given groupId.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} groupId The id of the group to leave.
 */
export const leaveGroup = async (sessionId, groupId) => {
  // create a rest client
  const authRestClient = await getAuthRestclient(sessionId);

  // call the leaveGroup API
  const leaveGroupResponse = await authRestClient.post("/group/leave", null, {
    params: {
      groupId,
    },
  });

  // console.log(leaveGroupResponse);
  await logApiResponse("leaveGroupResponse", leaveGroupResponse);
};
